import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@compiled/react';

import Button from '@atlaskit/button/standard-button';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VisibleOnFocusLink = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		opacity: 0,
		transition: 'opacity 0.75s',
		transitionDelay: '0.1s',
		pointerEvents: 'none',
		userSelect: 'none',
		'&:focus:hover': {
			cursor: 'pointer',
		},
		'&:focus': {
			opacity: 100,
			transition: 'opacity 0s',
			pointerEvents: 'auto',
			userSelect: 'auto',
		},
	},
});

export const useFocus = <T extends HTMLElement>(): [React.RefObject<T>, () => void] => {
	const focusRef = useRef<T>(null);
	const setFocus = () => {
		focusRef.current && focusRef.current.focus();
	};
	return [focusRef, setFocus];
};

/**
 * A link, hidden to mouse users, to aid in keyboard navigation
 * Only appears if focus is given (usually through tab-navigation).
 *
 */
export const SkipLink = ({ children, goTo }) => (
	<VisibleOnFocusLink
		onClick={goTo}
		onKeyPress={(event: React.KeyboardEvent) => {
			if (event.key === 'Enter' || event.key === 'Space') {
				goTo();
			}
		}}
		appearance="subtle-link"
		role="link"
	>
		{children}
	</VisibleOnFocusLink>
);

SkipLink.propTypes = {
	children: PropTypes.any.isRequired,
	setFocus: PropTypes.func,
};
